(() => {
  const PICKER_SELECTOR = '[data-limited-package-picker]';
  const MESSAGE_TEXT = {
    packageSoldOut: '選択したパッケージは現在品切れです。',
    packageSoldOutRollback: '選択したパッケージが品切れになりました。カート追加を取り消しました。',
    inventoryExceeded: '在庫数を超える数量はカートに追加できません。数量を減らしてください。',
    inventoryCheckFailed: '在庫確認に失敗しました。時間をおいて再度お試しください。',
    addFailed: 'カート追加に失敗しました。時間をおいて再度お試しください。',
  };

  const toInt = (value, fallback = 0) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const parseBoolean = (value) => String(value) === 'true';

  const formatMoney = (amountInCents, moneyFormat) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(amountInCents, moneyFormat);
      } catch (error) {
        console.error('limited-package: failed to format money', error);
      }
    }

    return `${(amountInCents / 100).toLocaleString('ja-JP')}円`;
  };

  class LimitedPackagePicker {
    constructor(container) {
      this.container = container;
      this.form = container.closest('form');
      this.productForm = container.closest('product-form');
      if (!this.form || !this.productForm) return;

      this.submitButton = this.form.querySelector('[type="submit"][name="add"]');
      this.variantIdInput = this.form.querySelector('.product-variant-id');
      this.quantityInput = null;
      this.message = container.querySelector('[data-limited-package-message]');
      this.total = container.querySelector('[data-limited-package-total]');
      this.cartHint = container.querySelector('[data-limited-package-cart-hint]');
      if (this.cartHint) {
        this.cartHint.textContent = '';
        this.cartHint.hidden = true;
      }
      this.optionButtons = Array.from(container.querySelectorAll('[data-limited-package-option]'));
      this.cart = document.querySelector('cart-drawer')
        || document.querySelector('cart-notification')
        || this.productForm.cart;

      this.baseProductId = String(container.dataset.baseProductId || '');
      this.baseProductHandle = String(container.dataset.baseProductHandle || '');
      this.baseProductTitle = String(container.dataset.baseProductTitle || '');
      this.basePriceCents = toInt(container.dataset.basePriceCents);
      this.baseInventoryCheck = parseBoolean(container.dataset.baseInventoryCheck);
      this.baseInventoryQuantity = toInt(container.dataset.baseInventoryQuantity, null);
      this.packageProductHandle = String(container.dataset.packageProductHandle || '');
      this.moneyFormat = container.dataset.moneyFormat || '${{amount}}';

      this.activeOption = null;
      this.isSubmitting = false;
      this.onQuantityChanged = () => this.updateTotal();

      this.quantityInput = this.resolveQuantityInput();

      this.optionButtons.forEach((button) => {
        button.addEventListener('click', () => this.onOptionClick(button));
      });
      this.bindQuantityListeners();

      this.form.addEventListener('submit', (event) => this.onSubmit(event), true);

      this.selectInitialOption();
      this.updateTotal();
      this.refreshCartHint();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          this.refreshCartHint();
        });
      }
    }

    getOptionData(button) {
      if (!button) return null;
      return {
        button,
        variantId: String(button.dataset.packageVariantId || ''),
        title: String(button.dataset.packageTitle || 'デフォルト'),
        priceCents: toInt(button.dataset.packagePriceCents),
        productHandle: String(button.dataset.packageProductHandle || ''),
        available: parseBoolean(button.dataset.packageAvailable ?? 'false'),
        inventoryCheck: parseBoolean(button.dataset.packageInventoryCheck),
        inventoryQuantity: toInt(button.dataset.packageInventoryQuantity, null),
      };
    }

    selectInitialOption() {
      const defaultOption =
        this.optionButtons.find((button) => (button.dataset.packageVariantId || '') === '') || this.optionButtons[0];
      if (!defaultOption) return;
      this.setActiveOption(defaultOption);
    }

    onOptionClick(button) {
      if (button.disabled) return;
      this.setActiveOption(button);
      this.showMessage('');
      this.updateTotal();
    }

    setActiveOption(button) {
      const selectedData = this.getOptionData(button);
      if (!selectedData) return;

      this.optionButtons.forEach((optionButton) => {
        const isActive = optionButton === button;
        optionButton.classList.toggle('is-active', isActive);
        optionButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.activeOption = selectedData;
    }

    getRequestedQuantity() {
      const fallbackQuantity = 1;
      this.bindQuantityListeners();
      this.quantityInput = this.resolveQuantityInput();
      if (!this.quantityInput) return fallbackQuantity;

      const min = toInt(this.quantityInput.min, fallbackQuantity);
      const rawValue = toInt(this.quantityInput.value, min);
      return Math.max(min, rawValue);
    }

    getProductScopeRoot() {
      return this.container.closest('product-info') || this.container.closest('[id^="ProductInfo-"]') || document;
    }

    getScopedQuantityInputs() {
      if (!this.form) return [];
      const scopeRoot = this.getProductScopeRoot();

      return Array.from(scopeRoot.querySelectorAll('input[name="quantity"]')).filter((input) => {
        if (input.disabled) return false;
        if (!input.form) return false;
        return input.form === this.form;
      });
    }

    resolveQuantityInput() {
      const inputs = this.getScopedQuantityInputs();
      if (inputs.length === 0) return null;

      const visibleInput = inputs.find((input) => input.offsetParent !== null);
      return visibleInput || inputs[0];
    }

    bindQuantityListeners() {
      this.getScopedQuantityInputs().forEach((input) => {
        if (input.dataset.limitedPackageQuantityBound === 'true') return;
        input.dataset.limitedPackageQuantityBound = 'true';
        input.addEventListener('change', this.onQuantityChanged);
        input.addEventListener('input', this.onQuantityChanged);
      });
    }

    getPropertyValue(item, key) {
      if (!item || !item.properties || typeof item.properties !== 'object') return '';
      const value = item.properties[key];
      if (value === null || value === undefined || value === '') return '';
      return String(value);
    }

    isPackageLine(item) {
      return this.getPropertyValue(item, '_linked_product_id') !== '';
    }

    createPickerOnlyError(message) {
      const error = new Error(message);
      error.showInPackagePickerOnly = true;
      return error;
    }

    getPackageInventoryExceededMessage(packageTitle) {
      const resolvedTitle = String(packageTitle || '選択したパッケージ').trim();
      return `${resolvedTitle}の在庫数を超えるため、カートに追加できませんでした。`;
    }

    isInventoryLimitErrorMessage(message) {
      return /最大数|在庫|数量|品切れ|stock|sold|quantity|available|maximum/i.test(String(message || ''));
    }

    getCartVariantQuantity(cart, variantId) {
      if (!cart || !Array.isArray(cart.items)) return 0;

      return cart.items.reduce((total, item) => {
        const itemVariantId = item.variant_id ?? item.id;
        if (String(itemVariantId) !== String(variantId)) return total;
        return total + toInt(item.quantity);
      }, 0);
    }

    validateInventoryRecords(cart, records) {
      const groupedRecords = new Map();

      records.forEach((record) => {
        if (!record || !record.variantId || !record.inventoryCheck || !Number.isFinite(record.inventoryQuantity)) return;
        const key = String(record.variantId);
        const currentRecord = groupedRecords.get(key) || {
          variantId: key,
          title: record.title || '',
          quantity: 0,
          inventoryQuantity: record.inventoryQuantity,
          isPackage: Boolean(record.isPackage),
        };
        currentRecord.quantity += toInt(record.quantity);
        currentRecord.inventoryQuantity = Math.min(currentRecord.inventoryQuantity, record.inventoryQuantity);
        currentRecord.isPackage = currentRecord.isPackage || Boolean(record.isPackage);
        groupedRecords.set(key, currentRecord);
      });

      if (groupedRecords.size === 0) return;
      if (!cart || !Array.isArray(cart.items)) {
        throw new Error(MESSAGE_TEXT.inventoryCheckFailed);
      }

      for (const record of groupedRecords.values()) {
        const currentQuantity = this.getCartVariantQuantity(cart, record.variantId);
        if (currentQuantity + record.quantity <= record.inventoryQuantity) continue;
        if (record.isPackage) {
          throw this.createPickerOnlyError(this.getPackageInventoryExceededMessage(record.title));
        }
        throw new Error(MESSAGE_TEXT.inventoryExceeded);
      }
    }

    buildPackageSetId(baseVariantId, packageVariantId) {
      const resolvedBaseVariantId = String(baseVariantId || '');
      const resolvedPackageVariantId = String(packageVariantId || 'default');
      return `base:${resolvedBaseVariantId}|package:${resolvedPackageVariantId}`;
    }

    updateTotal() {
      if (!this.total) return;

      const quantity = this.getRequestedQuantity();
      const packagePrice = this.activeOption ? this.activeOption.priceCents : 0;
      const amount = (this.basePriceCents + packagePrice) * quantity;
      this.total.textContent = `合計 ${formatMoney(amount, this.moneyFormat)}`;
    }

    showMessage(text, asError = false) {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.classList.toggle('is-error', asError);
    }

    async fetchCart() {
      const cartPath = window.routes?.cart_url || '/cart';

      try {
        const response = await fetch(`${cartPath}.js`, {
          headers: {
            Accept: 'application/json',
          },
        });
        if (!response.ok) return null;
        return response.json();
      } catch (error) {
        console.error('limited-package: failed to fetch cart', error);
        return null;
      }
    }

    async refreshCartHint() {
      if (!this.cartHint) return;
      this.cartHint.textContent = '';
      this.cartHint.hidden = true;
    }

    setSubmittingState(isSubmitting) {
      this.isSubmitting = isSubmitting;
      if (!this.submitButton) return;

      if (isSubmitting) {
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        const loadingSpinner = this.submitButton.querySelector('.loading__spinner') || this.form.querySelector('.loading__spinner');
        if (loadingSpinner) loadingSpinner.classList.remove('hidden');
        return;
      }

      this.submitButton.classList.remove('loading');
      if (!this.variantIdInput || !this.variantIdInput.disabled) {
        this.submitButton.removeAttribute('aria-disabled');
      }
      const loadingSpinner = this.submitButton.querySelector('.loading__spinner') || this.form.querySelector('.loading__spinner');
      if (loadingSpinner) loadingSpinner.classList.add('hidden');
    }

    getSectionsToRender() {
      if (!this.cart || typeof this.cart.getSectionsToRender !== 'function') return [];
      return this.cart
        .getSectionsToRender()
        .map((section) => section.id || section.section)
        .filter(Boolean);
    }

    getAddedQuantity(items, variantId) {
      if (!Array.isArray(items)) return 0;

      return items.reduce((total, item) => {
        if (String(item.id) !== String(variantId)) return total;
        return total + toInt(item.quantity);
      }, 0);
    }

    async postJson(url, payload) {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify(payload);

      const response = await fetch(url, config);
      const parsedResponse = await response.json();

      if (!response.ok || parsedResponse.status) {
        const message = parsedResponse.description || parsedResponse.message || MESSAGE_TEXT.addFailed;
        throw new Error(message);
      }

      return parsedResponse;
    }

    async refreshCartAfterMutation() {
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
        await cartDrawerItems.onCartUpdate();
        if (this.cart && typeof this.cart.open === 'function') {
          this.cart.open();
        }
        return;
      }

      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.onCartUpdate === 'function') {
        await cartItems.onCartUpdate();
      }
    }

    async rollbackBaseQuantity(baseLineKey, baseVariantId, previousBaseQuantity) {
      const sections = this.getSectionsToRender();
      const updates = {};
      if (baseLineKey) {
        updates[String(baseLineKey)] = previousBaseQuantity;
      } else {
        updates[String(baseVariantId)] = previousBaseQuantity;
      }
      const payload = {
        updates,
      };

      if (sections.length > 0) {
        payload.sections = sections;
        payload.sections_url = window.location.pathname;
      }

      try {
        const rollbackState = await this.postJson(window.routes?.cart_update_url || '/cart/update.js', payload);
        if (this.cart && rollbackState.sections && typeof this.cart.renderContents === 'function') {
          this.cart.renderContents(rollbackState);
        } else {
          await this.refreshCartAfterMutation();
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            cartData: rollbackState,
            productVariantId: String(baseVariantId),
          });
        }
      } catch (error) {
        console.error('limited-package: rollback failed', error);
      }
    }

    collectPackageHandles() {
      const handles = new Set();
      this.optionButtons.forEach((button) => {
        const handle = String(button.dataset.packageProductHandle || '').trim();
        if (handle) handles.add(handle);
      });
      if (handles.size === 0 && this.packageProductHandle) {
        handles.add(this.packageProductHandle);
      }
      return Array.from(handles);
    }

    async refreshPackageAvailability() {
      const packageHandles = this.collectPackageHandles();
      if (packageHandles.length === 0) return;

      const variantsById = new Map();

      try {
        await Promise.all(packageHandles.map(async (handle) => {
          const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) return;

          const packageProduct = await response.json();
          if (!packageProduct || !Array.isArray(packageProduct.variants)) return;

          packageProduct.variants.forEach((variant) => {
            variantsById.set(String(variant.id), Boolean(variant.available));
          });
        }));
      } catch (error) {
        console.error('limited-package: failed to refresh package availability', error);
        return;
      }

      let shouldResetToDefault = false;
      this.optionButtons.forEach((button) => {
        const variantId = String(button.dataset.packageVariantId || '');
        if (!variantId) return;

        const isAvailable = variantsById.get(variantId) === true;
        button.dataset.packageAvailable = isAvailable ? 'true' : 'false';
        button.hidden = !isAvailable;
        button.disabled = !isAvailable;
        button.classList.toggle('is-disabled', !isAvailable);

        if (!isAvailable && this.activeOption && this.activeOption.variantId === variantId) {
          shouldResetToDefault = true;
        }
      });

      if (shouldResetToDefault) {
        this.selectInitialOption();
        this.updateTotal();
      }
    }

    handleErrorMessage(errorOrMessage) {
      const message = errorOrMessage && errorOrMessage.message ? errorOrMessage.message : String(errorOrMessage || '');
      const showInPackagePickerOnly = Boolean(errorOrMessage && errorOrMessage.showInPackagePickerOnly);

      this.showMessage(message, true);
      if (!showInPackagePickerOnly && typeof this.productForm.handleErrorMessage === 'function') {
        this.productForm.handleErrorMessage(message);
      }
    }

    onSubmit(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (this.isSubmitting || this.submitButton?.getAttribute('aria-disabled') === 'true') return;
      if (!this.variantIdInput || this.variantIdInput.disabled) return;

      this.productForm.handleErrorMessage();
      this.showMessage('');
      this.updateTotal();
      this.handleSubmit().catch((error) => {
        this.handleErrorMessage(error || MESSAGE_TEXT.addFailed);
      });
    }

    async handleSubmit() {
      const baseVariantId = String(this.variantIdInput.value || '');
      if (!baseVariantId) {
        throw new Error(MESSAGE_TEXT.addFailed);
      }

      const selectedOption = this.activeOption || this.getOptionData(this.optionButtons[0]);
      if (!selectedOption || !selectedOption.button) {
        throw new Error(MESSAGE_TEXT.addFailed);
      }

      const packageLabel = selectedOption.title || 'デフォルト';
      const packageSetId = this.buildPackageSetId(baseVariantId, selectedOption.variantId || 'default');

      if (selectedOption.variantId && !selectedOption.available) {
        throw new Error(MESSAGE_TEXT.packageSoldOut);
      }

      const quantity = this.getRequestedQuantity();
      const sections = this.getSectionsToRender();

      if (this.cart && typeof this.cart.setActiveElement === 'function') {
        this.cart.setActiveElement(document.activeElement);
      }

      this.setSubmittingState(true);

      const cartBeforeAdd = await this.fetchCart();
      try {
        this.validateInventoryRecords(cartBeforeAdd, [
          {
            variantId: baseVariantId,
            title: this.baseProductTitle,
            quantity,
            inventoryCheck: this.baseInventoryCheck,
            inventoryQuantity: this.baseInventoryQuantity,
          },
          selectedOption.variantId
            ? {
                variantId: selectedOption.variantId,
                title: selectedOption.title,
                quantity,
                inventoryCheck: selectedOption.inventoryCheck,
                inventoryQuantity: selectedOption.inventoryQuantity,
                isPackage: true,
              }
            : null,
        ]);
      } catch (error) {
        this.setSubmittingState(false);
        throw error;
      }

      const previousBaseQuantity = cartBeforeAdd && Array.isArray(cartBeforeAdd.items)
        ? cartBeforeAdd.items.reduce((total, item) => {
            if (String(item.variant_id) !== baseVariantId) return total;
            if (this.isPackageLine(item)) return total;
            if (this.getPropertyValue(item, '_package_set_id') !== packageSetId) return total;
            return total + toInt(item.quantity);
          }, 0)
        : 0;

      const items = [
        {
          id: baseVariantId,
          quantity,
          properties: {
            _package_set_id: packageSetId,
            パッケージ: packageLabel,
          },
        },
      ];
      if (selectedOption.variantId) {
        items.push({
          id: selectedOption.variantId,
          quantity,
          properties: {
            _linked_product_id: this.baseProductId,
            _linked_product_handle: this.baseProductHandle,
            _linked_base_variant_id: baseVariantId,
            _package_set_id: packageSetId,
            '対象商品': this.baseProductTitle,
          },
        });
      }

      const payload = { items };
      if (sections.length > 0) {
        payload.sections = sections;
        payload.sections_url = window.location.pathname;
      }

      let addResponse;
      try {
        addResponse = await this.postJson(window.routes?.cart_add_url || '/cart/add.js', payload);
      } catch (error) {
        this.setSubmittingState(false);
        if (selectedOption.variantId && this.isInventoryLimitErrorMessage(error?.message)) {
          throw this.createPickerOnlyError(this.getPackageInventoryExceededMessage(selectedOption.title));
        }
        throw error;
      }

      if (!addResponse.key && Array.isArray(addResponse.items) && addResponse.items.length > 0) {
        addResponse.key = addResponse.items[0].key;
      }

      if (selectedOption.variantId) {
        const addedPackageQuantity = this.getAddedQuantity(addResponse.items, selectedOption.variantId);
        if (addedPackageQuantity < quantity) {
          const baseItem = Array.isArray(addResponse.items)
            ? addResponse.items.find((item) => {
                if (String(item.id) !== baseVariantId) return false;
                if (this.getPropertyValue(item, '_linked_product_id') !== '') return false;
                return this.getPropertyValue(item, '_package_set_id') === packageSetId;
              })
            : null;
          const baseLineKey = baseItem && baseItem.key ? String(baseItem.key) : '';

          await this.rollbackBaseQuantity(baseLineKey, baseVariantId, previousBaseQuantity);
          await this.refreshPackageAvailability();
          this.setSubmittingState(false);
          throw new Error(MESSAGE_TEXT.packageSoldOutRollback);
        }
      }

      if (!this.productForm.error && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          productVariantId: baseVariantId,
          cartData: addResponse,
        });
      }

      this.productForm.error = false;
      if (this.cart && this.cart.classList && this.cart.classList.contains('is-empty')) {
        this.cart.classList.remove('is-empty');
      }

      const hasRenderableSections = sections.length > 0
        && addResponse.sections
        && sections.every((sectionId) => Boolean(addResponse.sections[sectionId]));

      if (this.cart && hasRenderableSections && typeof this.cart.renderContents === 'function') {
        this.cart.renderContents(addResponse);
      } else if (this.cart) {
        await this.refreshCartAfterMutation();
      } else {
        window.location = window.routes.cart_url;
      }

      if (this.cart) {
        await this.refreshCartAfterMutation();
      }

      this.showMessage('');
      this.setSubmittingState(false);
      this.refreshCartHint();
    }
  }

  const setupLimitedPackagePickers = (root = document) => {
    root.querySelectorAll(PICKER_SELECTOR).forEach((picker) => {
      if (picker.dataset.limitedPackageBound === 'true') return;
      picker.dataset.limitedPackageBound = 'true';
      new LimitedPackagePicker(picker);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setupLimitedPackagePickers());
  } else {
    setupLimitedPackagePickers();
  }

  document.addEventListener('product-info:loaded', (event) => {
    if (!event.target) return;
    setupLimitedPackagePickers(event.target);
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target) return;
    setupLimitedPackagePickers(event.target);
  });
})();
